import {defineStore} from "pinia";
import {computed, type Ref, ref, shallowRef} from "vue";
import BasicCompanyDetails from "@/components/slides/BasicCompanyDetails.vue";
import CompanyMetricAndPreferences from "@/components/slides/CompanyMetricAndPreferences.vue";
import UserAccountSetup from "@/components/slides/UserAccountSetup.vue";
import RegistrationComplete from "@/components/slides/RegistrationComplete.vue";
import DuplicateEmail from "@/components/slides/DuplicateEmail.vue";
import {useRegistrationStore} from "@/stores/registration.ts";
import VerifyPhone from "@/components/slides/VerifyPhone.vue";
import PhoneVrified from "@/components/slides/PhoneVerified.vue";
import LeadPrices from "@/components/slides/LeadPrices.vue";
import LeadTypes from "@/components/slides/LeadTypes.vue";
import MonthlyBudget from "@/components/slides/MonthlyBudget.vue";
import LeadsPerDay from "@/components/slides/LeadsPerDay.vue";
import NumnerOfProducers from "@/components/slides/NumnerOfProducers.vue";
import InsuranceCarrierSelect from "@/components/slides/InsuranceCarrierSelect.vue";
import LeadStartDate from "@/components/slides/LeadStartDate.vue";
import BasicCompanyDetailsHowMuch from "@/components/slides/BasicCompanyDetailsHowMuch.vue";
import States from "@/components/slides/States.vue";
import {ORIGINS} from "@/services/ConstantSerice.ts";
import ScheduleMeeting from "@/components/slides/ScheduleMeeting.vue";
import TypeOfInsuranceBusiness from "@/components/slides/TypeOfInsuranceBusiness.vue";

export const SLIDES = {
    BASIC_COMPANY_DETAILS: 'basic_company_details',
    BASIC_COMPANY_DETAILS_HOW_MUCH: 'basic_company_details_how_much',
    COMPANY_METRIC_AND_PREFERENCES: 'company_metric_and_preferences',
    USER_ACCOUNT_SETUP: 'user_account_setup',
    REGISTRATION_COMPLETE: 'registration_complete',
    SCHEDULE_MEETING: 'schedule_meeting',
    DUPLICATE_EMAIL: 'duplicate_email',
    VERIFY_PHONE: 'verify_phone',
    PHONE_VERIFIED: 'phone_verified',
    LEAD_PRICES: 'lead_prices',
    LEAD_TYPES: 'lead_types',
    MONTHLY_BUDGET: 'monthly_budget',
    LEADS_PER_DAY: 'leads_per_day',
    NUMBER_OF_PRODUCERS: 'number_of_producers',
    INSURANCE_CARRIER_SELECT: 'insurance_carrier_select',
    LEAD_START_DATE: 'lead_start_date',
    LEAD_STATES: 'states',
    TYPE_OF_INSURANCE_BUSINESS: 'type_of_insurance_business',
}

export const REDIRECTABLE_SLIDES = [
  SLIDES.SCHEDULE_MEETING
];

export const useSlidesStore = defineStore('slides', () => {
    const currentSlide: Ref<string> = ref(SLIDES.BASIC_COMPANY_DETAILS);
    const componentMap = shallowRef({
        [SLIDES.BASIC_COMPANY_DETAILS]: BasicCompanyDetails,
        [SLIDES.BASIC_COMPANY_DETAILS_HOW_MUCH]: BasicCompanyDetailsHowMuch,
        [SLIDES.COMPANY_METRIC_AND_PREFERENCES]: CompanyMetricAndPreferences,
        [SLIDES.USER_ACCOUNT_SETUP]: UserAccountSetup,
        [SLIDES.REGISTRATION_COMPLETE]: RegistrationComplete,
        [SLIDES.SCHEDULE_MEETING]: ScheduleMeeting,
        [SLIDES.DUPLICATE_EMAIL]: DuplicateEmail,
        [SLIDES.VERIFY_PHONE]: VerifyPhone,
        [SLIDES.PHONE_VERIFIED]: PhoneVrified,
        [SLIDES.LEAD_PRICES]: LeadPrices,
        [SLIDES.LEAD_TYPES]: LeadTypes,
        [SLIDES.MONTHLY_BUDGET]: MonthlyBudget,
        [SLIDES.LEADS_PER_DAY]: LeadsPerDay,
        [SLIDES.NUMBER_OF_PRODUCERS]: NumnerOfProducers,
        [SLIDES.INSURANCE_CARRIER_SELECT]: InsuranceCarrierSelect,
        [SLIDES.LEAD_START_DATE]: LeadStartDate,
        [SLIDES.LEAD_STATES]: States,
        [SLIDES.TYPE_OF_INSURANCE_BUSINESS]: TypeOfInsuranceBusiness,
    });
    
    const initialSlides = ref([SLIDES.BASIC_COMPANY_DETAILS, SLIDES.BASIC_COMPANY_DETAILS_HOW_MUCH]);

    const registrationStore = useRegistrationStore();

    const hideNextButton = computed(() => {
        return [
            SLIDES.REGISTRATION_COMPLETE,
            SLIDES.DUPLICATE_EMAIL,
            SLIDES.VERIFY_PHONE,
            SLIDES.PHONE_VERIFIED,
            SLIDES.SCHEDULE_MEETING,
        ].includes(currentSlide.value);
    });
    const hideBackButton = computed(() => {
        //todo: implement when needed
        return true;
    });
    
    const setInitialSlide = (slide: string) => {
        currentSlide.value = slide;
    }

    const nextSlide = (slide?: string) => {
        if (slide) {
            currentSlide.value = slide;
            return;
        }

        switch (currentSlide.value) {
            case SLIDES.BASIC_COMPANY_DETAILS:
            case SLIDES.BASIC_COMPANY_DETAILS_HOW_MUCH:
                if (!registrationStore.registeringCompany.emailCanLogin) {
                    currentSlide.value = SLIDES.DUPLICATE_EMAIL;
                } else {
                    currentSlide.value = SLIDES.VERIFY_PHONE;
                }

                break;
            case SLIDES.VERIFY_PHONE:
                currentSlide.value = SLIDES.PHONE_VERIFIED;
                break;
            case SLIDES.USER_ACCOUNT_SETUP:
                if (registrationStore.registrationOrigin?.toLowerCase() === ORIGINS.HM) {
                    currentSlide.value = SLIDES.REGISTRATION_COMPLETE;    
                } else {
                    currentSlide.value = SLIDES.LEAD_PRICES;   
                }
                break;
            case SLIDES.LEAD_PRICES:
                currentSlide.value = SLIDES.REGISTRATION_COMPLETE;
                break;
            case SLIDES.LEAD_TYPES:
                currentSlide.value = SLIDES.USER_ACCOUNT_SETUP;
                break;
            case SLIDES.LEAD_STATES:
                currentSlide.value = SLIDES.TYPE_OF_INSURANCE_BUSINESS;
                break;
            case SLIDES.TYPE_OF_INSURANCE_BUSINESS:
                currentSlide.value = SLIDES.INSURANCE_CARRIER_SELECT;
                break;
            case SLIDES.INSURANCE_CARRIER_SELECT:
                currentSlide.value = SLIDES.LEAD_TYPES;
                break;    
            case SLIDES.MONTHLY_BUDGET:
                currentSlide.value = SLIDES.LEADS_PER_DAY;
                break;
            case SLIDES.LEADS_PER_DAY:
                currentSlide.value = SLIDES.NUMBER_OF_PRODUCERS;
                break;
            case SLIDES.NUMBER_OF_PRODUCERS:
                currentSlide.value = SLIDES.LEAD_START_DATE;
                break;
            case SLIDES.LEAD_START_DATE:
                currentSlide.value = SLIDES.INSURANCE_CARRIER_SELECT;
                break;
        }
    }

    const previousSlide = () => {
        //todo: implement when needed
    }

    return {
        currentSlide,
        componentMap,
        hideNextButton,
        hideBackButton,
        initialSlides,
        nextSlide,
        previousSlide,
        setInitialSlide
    }
});
